const algorithmCategories = [
    'ALL',
    'FREQUENCY COUNTER',
    'POINTERS',
    'OBJECT ORIENTED',
    'RECURSION',
    'SEARCH',
    'SORT',
    'ADVANCED ARRAY',
    'CLOSURE'
];

const algorithmData = [
    {
        title: 'isAnagram',
        categories: ['ALL', 'FREQUENCY COUNTER', 'OBJECT ORIENTED'],
        description: 'Given two strings, write a function to determine if the second string is an anagram of the first. An anagram is a word, phrase, or name formed by rearranging the letters of another, such as cinema, formed from iceman.',
        myMethodology: 'To start, I determine that nothing is an Anagram if the lengths are not the same. This is mostly to cut the function short if the circumstances can not be met. I then loop through each letter from the first string and by using a JavaScript Object, I create keys to act as counters for each letter. I then loop through the second string. If the letter from the second string does not have a value in the counter from the first string, then the function will return false. Otherwise, deduct one value from that key on the object and continue the loop. Should the second string run all of the way through, the function will return true.',
        code: `function isAnagram(str1, str2) {
            if (str1.length !== str2.length) {
                return false;
            }
        
            let frequency = {};
        
            for (let i = 0; i < str1.length; i++) {
                let letter = str1[i];
                frequency[letter] ? frequency[letter] += 1 : frequency[letter] = 1;
            }
        
            for (let i = 0; i < str2.length; i++) {
                let letter = str2[i];
                if (!frequency[letter]) {
                    return false;
                } else {
                    frequency[letter] -= 1;
                }
            }
            return true;
        }`
    },
    {
        title: 'countUniqueValues',
        categories: ['ALL', 'FREQUENCY COUNTER', 'OBJECT ORIENTED'],
        description: 'Implement a function called countUniqueValues, which accepts an array, and counts the unique values in the array.',
        myMethodology: 'Originally, the Array was required to be sorted, however I created a work-around. First I use an object to create a counter for each value. I then loop through the array being inserted into the function. If a value in the array is not a key in the object, one will be made. If there is already one, simply increase the value of that key by one and move on. Return the length of the objects keys, which will count the number of unique values contained within the array.',
        code: `function countUniqueValues(arr) {
            let counter = {};
            for (let i = 0; i < arr.length; i++) {
                if (counter[arr[i]] === undefined) counter[arr[i]] = 1; 
                else counter[arr[i]] += 1;
            }
            return Object.keys(counter).length;
        }`
    },
    {
        title: 'isSubsequence',
        categories: ['ALL', 'POINTERS'],
        description: 'Write a function called isSubsequence which takes in two strings and checks whether the characters in the first string form a subsequence of the characters in the second string. In other words, the function should check whether the characters in the first string appear somewhere in the second string, without their order changing.',
        myMethodology: 'I start off by determining that the function is false if the second string is shorter than the first string. Then, I create two pointers. One is to count the number of matching values. The other is to assist in tracking the current index of the position and where the counter should start from in the second string when the iteration adds to the counter. I then loop through the second string. If the character that is currently being counted from in the first string matches the character being tracked following the position of the counter, then three things happen. The counter increases by one, the assisted index becomes the current value of "i" at the time the characters matched in order to prevent any of the values prior to "i" from being accounted for, and then "i" is reset to -1 in order to reset the values to be where they need to be in order to continue the character from the second string to match the index of where the counter from the first string is. This repeats until the counters value is equal to the length of the first string. If the entire second string is looped through without the counter equaling that length, then the function returns false.',
        code: `function isSubsequence(str1, str2) {
            if (str2.length < str1.length) return false;
            let counter = 0;
            let indexAt = 0;
            for (let i = 0; i < str2.length; i++) {
                if (str1[counter] === str2[i + indexAt + counter]) {
                    counter++;
                    indexAt = i;
                    i = -1;
                }
                if (counter === str1.length) return true;
            }
            return false;
        }`
    },
    {
        title: 'fib',
        categories: ['ALL', 'RECURSION'],
        description: 'Write a recursive function called fib which accepts a number and returns the nth number in the Fibonacci sequence. Recall that the Fibonacci sequence is the sequence of whole numbers 1, 1, 2, 3, 5, 8, ... which starts with 1 and 1, and where every number thereafter is equal to the sum of the previous two numbers.',
        myMethodology: 'First, 0 is determined to be 0 since it does not start in the Fibonacci Sequence. Then, 1 is immediately recognized as the first number of the Fibonacci Sequence. From this point on, we calculate the nth number by recursively calling this function, but then add the current number we insert as a parameter deducted by two with that same number deducted by 1.',
        code: `function fib(num) {
            if (num === 0) return 0;
            if (num === 1) return 1;
            return fib(num - 2) + fib(num - 1);
        }`
    },
    {
        title: 'flatten',
        categories: ['ALL', 'RECURSION'],
        description: 'Write a recursive function called flatten which accepts an array of arrays and returns a new array with all values flattened. (There are no empty arrays within the array.)',
        myMethodology: 'An empty array is first created which will be returned once the function ends each time the array is executed. A flattened version of an array is then created by concatting all of the elements from the array and creating a new array with just those elements by using the spread operator. Then, for each element in the array check if that element is an array. If the element is an array, recursively call this function again to now check if there are arrays inside of that array, otherwise push the value inside of the array to be returned at the end. This process repeats until there are no arrays. Once all of the elements have been accounted for, return an array where all of the elements that were not arrays were pushed.',
        code: `function flatten(arrOfArrs) {
            let arr = [];
            let flatArr = [].concat(...arrOfArrs);
            for (let i = 0; i < arrOfArrs.length; i++) {
                if (Array.isArray(arrOfArrs[i])) return flatten(flatArr);
                else arr.push(arrOfArrs[i]);
            }
            return arr;
        }`
    },
    {
        title: 'collectStrings',
        categories: ['ALL', 'OBJECT ORIENTED', 'RECURSION'],
        description: 'Write a function called collectStrings which accepts an object and returns an array of all the values in the object that have a type of string.',
        myMethodology: 'First, an empty array is included in the parameter to prevent the need for a helper function. The object inserted into the parameter then has the keys looped through. The value for each key is first checked to see if the type is a string, if it is then the value is pushed into the array. However, if the value of the objects key is an object, the function is now executed where the current key is used as the parameter for the function, as well as the array that the strings are being pushed into. Once the object and all nested values have been iterated through, the array where the strings were pushed into is then returned.',
        code: `function collectStrings(obj, arr = []) {
            for (let key in obj) {
                if (typeof obj[key] === 'string') arr.push(obj[key]);
                if (typeof obj[key] === 'object') collectStrings(obj[key], arr);
            }
            return arr;
        }`
    },
    {
        title: 'linearSearch',
        categories: ['ALL', 'SEARCH'],
        description: 'Write a function called linearSearch which accepts an array and a value, and returns the index at which the value exists without utilizing the indexOf function. If the value does not exist in the array, return -1.',
        myMethodology: 'Begin by looping through the array inserted into the parameter. If the value being looped through in the array is equal to the value of the index in that array, return that value. The value by default was returned as a String, so I decided to parse it into a number. If the array is looped through and the value being searched was not found in the array, return -1.',
        code: `function linearSearch(arr, val) {
            for (let value in arr) {
                if (arr[value] === val) return parseInt(value);
            }
            return -1;
        }`
    },
    {
        title: 'binarySearch',
        categories: ['ALL', 'SEARCH'],
        description: 'Write a function called binarySearch which accepts a sorted array and a value and returns the index at which the value exists. Otherwise, return -1. This algorithm is longer, but more efficient than Linear Search. The downside being that it must accept a sorted array in order to function.',
        myMethodology: 'Create three pointers in the array, one will symbolize the left element of the array (the first element), the second being the right (the last element), and the third one being the middle of the array (The length of the array split in half). Using the middle pointer, a divide and conquer method can be utilized to split the array in half by checking whether or not the value is greater than the middle point in the array. First, it is checked to see if the value is equal to any of the pointers positions within the array and if so then the function will return the value of that pointer which is the index that the pointer is currently keeping track of, otherwise it will run a while loop with multiple conditions. The while loop will ensure that middle is neither equal to the left or right pointers to prevent infinite looping. It will also check to make sure that the value is not less than or greater than any value within the loop to immediately shut out any values outside of the range of values within the array. Inside of the loop, if the element at the middle point in the index is greater than the value, the right pointer is moved to where the middle currently is and the middle pointer is then moved in the new middle point between the current right and left pointer values within the array. If the value at the index of the middle pointer is less than the value, the left pointer is moved to the middle pointer and the middle pointer is moved in the middle point between the current right and left pointer values within the array. (This is determined by averaging the left and right pointer.) Then, if the value at the index of the updated middle pointer is equal to the value being checked for, that pointer is returned. If the while loop runs through and no values were found in the array that match the value inserted into the function, then the function returns -1.',
        code: `function binarySearch(arr, val) {
            let left = 0;
            let right = arr.length - 1;
            let middle = Math.floor(arr.length / 2);
            if (arr[left] === val) return left;
            if (arr[middle] === val) return middle;
            if (arr[right] === val) return right;
            while (middle !== left && middle !== right && val > arr[left] && val < arr[right]) {
                if (arr[middle] > val) {
                    right = middle;
                    middle = Math.floor((left + right) / 2);
                }
                if (arr[middle] < val) {
                    left = middle;
                    middle = Math.floor((left + right) / 2);
                }
                if (arr[middle] === val) return middle;
            }
            return -1;
        }`
    },
    {
        title: 'swap',
        categories: ['ALL', 'SORT'],
        description: 'These are two different swap functions. For the sake of efficiency, I primarily use the one that utilizes array destructuring, however both are viable options.',
        myMethodology: 'The first one utilizes array destructuring. It accepts an array as well as two values. It simultaneously states that the value of the array at the index of the first inserted index and the value of the array at the index of the second inserted value are now the reverse. So, the value in the array at idx1 is the value in the array at idx2 while the value in the array at idx2 is the value in the array at idx1. The second function does the same thing, but differently. It creates a temporary variable to clone the value of one index, then turns the value of that index into the value of the other index, then finally turns the value of the second index into the clone of the first one.',
        code: `const swap = (arr, idx1, idx2) => {
            [arr[idx1], arr[idx2]] = [arr[idx2], arr[idx1]];
        }
        
        function swap(arr, idx1, idx2) {
            let temp = arr[idx1];
            arr[idx1] = arr[idx2];
            arr[idx2] = temp;
        }`
    },
    {
        title: 'bubbleSort',
        categories: ['ALL', 'SORT'],
        description: 'Using the bubble sort method, iterate through an array and rearrange the values of the array so that they are in order.',
        myMethodology: 'First, loop through the array being inserted into the function. Start by using the last element as a stopping point and then begin looping from the first element with another loop. Starting from the first element, check to see if it is larger then the following element, if it is, then swap them. Otherwise, keep looping. This will continue until the index where "i" is, once "i" has been reached, the loop will start all over again with the length being one less. This process will repeat until all of the elements are in the correct order, and then the array with the correct order is finally returned.',
        code: `function bubbleSort(arr) {
            for (let i = arr.length; i > 0; i--) {
                for (let j = 0; j < i - 1; j++) {
                    if (arr[j] > arr[j + 1]) {
                        [arr[j + 1], arr[j]] = [arr[j], arr[j + 1]]
                    }
                }
            }
            return arr;
        }`
    },
    {
        title: 'selectSort',
        categories: ['ALL', 'SORT'],
        description: 'Using the select sort method, iterate through an array and rearrange the values of the array so that they are in order.',
        myMethodology: 'First, loop through the array being inserted into the function. Create a clone of "i" that will act as a minimum value throughout the array for swapping in case lower values are found by the end of a nested loop. Inside of the nested loop, determine whether or not that the value at index "j" is less than the value at the index of the clone of "i". If it is, then have the clone of "i" now become a clone of "j". Keep doing this, having the clone of "j" change to the new "j" each time the value is greater than the new "j". Once this is done, determine if the clone of "i" had ever changed (when looping through the nested loop). If it had, then have the element at the index of where "i" is swap values with the element at the index where the clone of "i" (which was changed to a clone of "j" due to it becoming a smaller value) is indexed at. Repeat this process through each iteration of "i" until the end of the array. Afterwards, a sorted version of the array will be returned.',
        code: `function selectSort(arr) { 
            for (let i = 0; i < arr.length; i++) {
                let min = i;
                for (let j = i + 1; j < arr.length; j++) {
                    if (arr[j] < arr[min]) min = j;
                }
                if (min !== i) [arr[i], arr[min]] = [arr[min], arr[i]];
            }
            return arr;
        }`
    },
    {
        title: 'quickSort',
        categories: ['ALL', 'SORT', 'POINTERS', 'RECURSION'],
        description: 'Using the quick sort method, iterate through an array and rearrange the values of the array so that they are in order.',
        myMethodology: 'First, create a pivot function. This function will be used to extract a pivot element that will act to partition other elements into subarrays. It starts by defining a pivot, which will default at the value of the first defined index of the array. Then, a swapIndex variable is defined to determine the index in which values will be swapped later on and is defined to be the first inserted index. A loop is then called which starts at the index where the start index begins, and ends where the end index ends. (This is because we are not going to loop through the whole array each time, rather the section of the array we will be pivoting each time that it is needed.) If the current value of the pivot is greater than the current index in the array of the loop, the value of swapIndex is increased and then the values at the array of where swapIndex currently is and the value of where "i" currently is swap. This process repeats until the end of the pivoting, after which the value at the array where the pivoting starts will then be swapped with the value of the array where the swapIndex was tracked. And then finally the swapIndex is the returned value. Now that Pivot is defined,  the quickSort function can be executed in order to achieve what is needed. First, a conditional to determine if the left value is less than the right value is used to help recursively sort subarrays. Inside of the conditional, a variable is created in order to capture the value of the swapIndex from the pivot function, then quicksort is recursively called on the first half and the second half of the array. When the arrays are too small to recursively call anymore (when left is no longer less than right), the function finally returns the array and removes it from the stack. This process repeats until all of the recursively called functions are executed, in which a sorted array is finally returned.',
        code: `function quickSort(array, left = 0, right = array.length - 1) {
            if (left < right) {
                let swapIdx = pivot(array, left, right);
                quickSort(array, left, swapIdx - 1);
                quickSort(array, swapIdx + 1, right);
            }
            function pivot(arr, start = 0, end = arr.length - 1) {
                let piv = arr[start];
                let swapIndex = start;
                for (let i = start; i <= end; i++) {
                    if (piv > arr[i]) {
                        swapIndex++;
                        [arr[swapIndex], arr[i]] = [arr[i], arr[swapIndex]];
                    }
                }
                [arr[start], arr[swapIndex]] = [arr[swapIndex], arr[start]];
                return swapIndex;
            }
            return array;
        }`
    },
    {
        title: 'radixSort',
        categories: ['ALL', 'SORT', 'OBJECT ORIENTED'],
        description: 'Using the radix sort method, iterate through an array and rearrange the values of the array so that they are in order.',
        myMethodology: 'There are three written functions before executing this Radix Sorting algorithm. The first is a getDigit function, which is used to help get the digit in the number at the given place value. The way it will work is that it accepts a number and an index, then it takes the absolute version "-/+" of that number and divides it by 10 to the "i"th power, rounded down. Finally, by using modulo, the remainder is taken out which will return the value of the digit based on what the index (i) was. The second function is called digitCount and what this does is take in a number and counts the number of digits in that number. This is useful because it is needed in order to determine which number has the most digits, as it will be needed to set the rest of the numbers to have the same number of digits, but the ones with less digits will have 0s to the left of them. This function returns 1 if the number is 0 (to prevent getting -Infinity as a returned value), otherwise it returns a count of the digits using Math functions. The first thing to do is take a number and make it absolute "-/+", then use a logarithm of ten on that number and round it down. Finally, take that number and add one. For example, log10(9999) rounded down is three, then when you add one to that it becomes four. The third function is used to get the maximum number of digits from all numbers inside of an array. To do this, it simply implements the digitCount function on each element of an array and tracks the maximum number of digits by using the digitCount function and comparing it to the current maximum number of digits recorded. After the array has been looped through, it returns what the maximum number of digits was. With these three functions, radix sort can finally be executed. First, the maximum number of digits is tracked using the mostDigits function then loops a number of times based on the maximum digits. Inside of the loop, create a digitBucket, which is an array of ten arrays that will be used to track the current digit for each value and inserting it into those arrays. Then, loop through each array and grab the current digit of each number in the array, and place it inside of the array that shares an index with that digit. Finally, have nums concat the arrays from digitBuckets using the spread operator so that way it pushes all of the values into a single array. Once each digit has been looped through, nums will return sorted.',
        code: `// Radix Sort utilizes the first three functions
        function getDigit(num, i) {
            return Math.floor(Math.abs(num) / Math.pow(10, i)) % 10;
        }
        
        function digitCount(num) {
            if (num === 0) return 1
            return Math.floor(Math.log10(Math.abs(num))) + 1;
        }
        
        function mostDigits(nums) {
            let max = 0;
            for (num of nums) {
                max = Math.max(max, digitCount(num));
            }
            return max;
        }
        
        function radixSort(nums) {
            let max = mostDigits(nums);
            for (let i = 0; i < max; i++) {
                let digitBuckets = Array.from({ length: 10 }, () => []);
                for (let j = 0; j < nums.length; j++) {
                    let digit = getDigit(nums[j], i);
                    digitBuckets[digit].push(nums[j]);
                }
                nums = [].concat(...digitBuckets);
            }
            return nums;
        }`
    },
    {
        title: 'reducerVowelCount',
        categories: ['ALL', 'ADVANCED ARRAY', 'OBJECT ORIENTED'],
        description: 'Write a function called reducerVowelCount which accepts a string and returns an object with the keys as the vowel and the values as the number of times the vowel appears in the string. This function should be case insensitive so a lowercase letter and uppercase letter should count',
        myMethodology: 'First, make a string to account for all of the vowels. Then return the string from the parameter as an array by using the split function and use the Array.reduce function from that to check if each letter in the array is a vowel or not by using indexOf on the vowels string. Have the accumulator initialize as an empty object in order to create key/value pairs to count each vowel. To prevent capitalization, all of the values being pased into the reduce function are lowercased prior to checking. Then, check if the vowel is already in the accumulator object. If it is not, then place it in with the value of one, otherwise add one to it. Finally, return the accumulator object which will contain keys for each vowel from the string as well as a count of the number of times that vowel was used in the string.',
        code: `function reducerVowelCount(str) {
            const vowels = 'aeiou';
            return str.split("").reduce((accumulator, nextValue) => {
                if (vowels.indexOf(nextValue.toLowerCase()) !== -1) {
                    if (nextValue in accumulator) accumulator[nextValue] += 1; 
                    else accumulator = 1;
                }
                return accumulator;
            }, {})
        }`
    },
    {
        title: 'sumDoubleOddArguments',
        categories: ['ALL', 'ADVANCED ARRAY'],
        description: 'Write a function called sumDoubleOddArguments which takes all of the arguments passed to a function and returns the sum of the odd ones and then doubles it.',
        myMethodology: 'A variable is first created to store all of the arguments passed from the function into an array. This is done by using array.slice to create a copy of an empty array and using the call method on the arguments object to set them as the value of "this" for the array.slice method. Then, return the array of arguments with a reduce function and have the base value set to 0. For each argument from the array, check if the number is odd by using modulo. If it is odd, add the value of that argument doubled to the accumulator, otherwise return the accumulator and have it remain unchanged. Once this continues through all of the arguments, the total value that the accumulator is set as will be returned.',
        code: `function sumDoubleOddArguments() {
            const args = [].slice.call(arguments);
            return args.reduce((accumulator, next) => {
                if (next % 2 !== 0)  return accumulator + next * 2;
                else return accumulator;
            }, 0)
        }`
    },
    {
        title: 'flip',
        categories: ['ALL', 'CLOSURE', 'ADVANCED ARRAY'],
        description: 'Write a function called flip which accepts a function and a value for the keyword this. Flip should return a new function that when invoked, will invoke the function passed to flip with the correct value of the keyword this and all of the arguments passed to the function reversed.',
        myMethodology: 'First create an empty array to store the arguments in a reversed order with. Then define the outer arguments using array.slice.call method on the arguments. For the outerArgs variable, the number two is passed in so  that the array will start from the index of two with it. This is to bypass the first two arguments of the function, the callback and the argument that is acting as a value for the keyword "this". Then return a function (making use of closure) to create another variable for the inner arguments, which is just an array.slice.call on the arguments since no other arguments are going to be passed in. Then, a third variable is created called allArgs which concats the two argument variables together and creates a copy that is the full length of the callback function. Then, loop through each argument and place it in the beginning of the empty array that was created so that order of all of the arguments is reversed. Return the callback function were "this" is the reversed array from all of the arguments that were passed in.',
        code: `function flip(fn, thisArg) {
            const arr = [];
            const outerArgs = [].slice.call(arguments, 2)
            return function () {
                const innerArgs = [].slice.call(arguments);
                const allArgs = outerArgs.concat(innerArgs).slice(0, fn.length);
                for (let arg of allArgs) {
                    arr.unshift(arg);
                }
                return fn.apply(thisArg, arr);
            }
        }`
    },
]

const imageData = [
    {
        img: 'images/Mobile-Images/Niagara-Falls-1.JPG',
        title: 'Niagara Falls'
    },
    {
        img: 'images/Mobile-Images/Top-Golf-1.PNG',
        title: 'Top Golf'
    },
    {
        img: 'images/Mobile-Images/Guitar-1.jpg',
        title: 'Acoustic Guitar'
    },
    {
        img: 'images/Mobile-Images/Baseball-1.JPG',
        title: 'Baseball'
    },
    {
        img: 'images/Mobile-Images/Algorithms-Board-1.JPG',
        title: 'Algorithms'
    },
    {
        img: 'images/Mobile-Images/Disney-1.JPG',
        title: 'Excalibur'
    },
    {
        img: 'images/Mobile-Images/Hockey-1.JPG',
        title: 'Hockey'
    },
    {
        img: 'images/Mobile-Images/Coding-1.JPG',
        title: 'Coding'
    },
    {
        img: 'images/Mobile-Images/Guitar-2.PNG',
        title: 'Electric Guitar'
    },
    {
        img: 'images/Mobile-Images/Snowboarding-1.JPG',
        title: 'Snowboarding'
    }
];

const certificates = [
    {
        title: 'The Web Developer Bootcamp by Colt Steele',
        link: '/images/Certifications/Web-Developer-Bootcamp-Certificate.jpg',
        skills: 'HTML5 • CSS3 • Bootstrap • Modern JavaScript • AJAX/API Calls • EJS • NodeJs • ExpressJS • RESTful Routing • MongoDB • Authorization/Authentication'
    },
    {
        title: 'The Ultimate MySQL Bootcamp by Colt Steele',
        link: '/images/Certifications/MySQL-Certificate.jpg',
        skills: 'Creating SQL Tables • Interacting with Existing Databases • Model Real World Data • Generate Reports • Work With Large Data Sets using MySQL'
    },
    {
        title: 'Algorithms and Data Structures by Colt Steele',
        link: '/images/Certifications/ADS-Certificate.jpg',
        skills: 'Algorithm Patterns • Building Data Structures • Tree Traversal • Sorting • Stacks and Queues • Algorithms • Improved Problem Solving Skills • Solve Dijkstras Algorithm '
    },
    {
        title: 'Advanced Web Developer Bootcamp by Colt Steele',
        link: '/images/Certifications/Advanced-Web-Developer-Certificate.jpg',
        skills: 'Advanced CSS • D3 • SVG • Advanced Array Methods • Object Oriented Programming • Testing With Jasmine'
    },
    {
        title: 'The Modern React Bootcamp by Colt Steele',
        link: '/images/Certifications/Modern-React-Certificate.jpg',
        skills: 'JSX • React Props • State • React CRUD • LifeCycle Methods • React Router • React Hooks • Context • useReducer and useContext • NextJS'
    },
    {
        title: 'The Git and Github Bootcamp by Colt Steele',
        link: '/images/Certifications/Git-Certificate.jpg',
        skills: 'Terminal Git Commands • Commit • Branch • Merge • Stash • Diff • Restore • Revert • Reset • Fork • Clone • Reflog • Collaboration'
    }
];

const projects = [
    {
        title: 'Marvel Comic Finder',
        image: '/images/Project-Images/marvel-app.png',
        description: 'A Marvel Comic Book finder I created with various filters using the Marvel API and React. Data Provided by Marvel. © 2014 Marvel',
        link: 'marvel-app'
    },
    {
        title: 'Color App',
        image: '/images/Project-Images/color-app-4.png',
        description: 'A beautiful and fully functional color app clone. Unlike the original, I built this one using React Hooks and Modern Node Packages.',
        link: 'color-app'
    },
    {
        title: 'React Todo List',
        image: '/images/Project-Images/todo-list.png',
        description: 'A simple todo list that uses full CRUD. This was a project from Colt Steeles React Course, but I made some small tweaks.',
        link: 'todo-list'
    }
]

export { algorithmCategories, algorithmData, imageData, certificates, projects };